import { AnyAri } from '@atlassian/ari/any-ari';
import { getRelativeTimeAndUnit, type IntlShape } from '@atlassian/jira-intl';

import {
	WORKS_SUGGESTIONS_TYPES,
	SECONDS_IN_DAY,
	SECONDS_IN_HOUR,
	SECONDS_IN_MINUTE,
} from './constants';

const REG = /.+\/(?<taskId>[\w.~_-]+)$/;

export const getUuidFromTask = ({ id, typename }: { id: string; typename: string }): string => {
	const taskType = WORKS_SUGGESTIONS_TYPES[typename];
	try {
		const { resourceId } = AnyAri.parse(id);
		const { groups } = REG.exec(resourceId) || {};
		if (groups) {
			return `${groups.taskId}:${taskType}`;
		}
		return `${id}:${taskType}`;
	} catch (Error) {
		return `${id}:${taskType}`;
	}
};

// Remove this when cleanup `inxs-suggestion-id-sync-with-ers-enabled` flag
export const isTaskTypeInId = (id: string, typename: string): boolean => {
	const taskType = WORKS_SUGGESTIONS_TYPES[typename];
	if (!taskType) {
		return false;
	}
	return id.endsWith(taskType);
};

/**
 * This method is a wrapper around {formatRelativeTime} to fix a library error.
 * E.g. while using {formatRelativeTime}, when the time goes back to the previous year,
 * even if less than a month ago, instead of rendering the time in days, it generates "1 year ago".
 */
export const formatTimeLapse = (
	// eslint-disable-next-line @typescript-eslint/no-explicit-any
	formatRelativeTime: (value: any, unit?: any) => string,
	date: string,
) => {
	const { time: timeInitial, unit } = getRelativeTimeAndUnit(date);
	if (['second', 'minute', 'hour'].includes(unit)) {
		return formatRelativeTime(timeInitial, unit);
	}
	const { time } = getRelativeTimeAndUnit(date, 'day');
	return formatRelativeTime(time, 'day');
};

export function sortBasedOnOrder(
	list: {
		readonly __typename: string;
		readonly id: string;
		node: React.JSX.Element | null;
	}[],
	order: readonly string[],
) {
	// Create a mapping from item to its order for quick lookup
	const orderMap = new Map(order.map((item, index) => [item, index + 1]));
	const maxOrder = order.length + 1;

	// Sort the list based on the order defined in orderMap
	const sortedList = list.sort(({ id: idX }, { id: idY }) => {
		// Get order of a and b. If not found, assign a default order that puts them at the end.
		const orderA = orderMap.get(idX) || maxOrder;
		const orderB = orderMap.get(idY) || maxOrder;
		return orderA - orderB;
	});
	return sortedList;
}

export function sortOnScoreOrder(
	list: {
		readonly __typename: string;
		readonly id: string;
		node: React.JSX.Element | null;
		orderScore: {
			major: number;
			minor: number;
		} | null;
	}[],
) {
	// Sort the list based on the order defined in orderMap
	const sortedList = list.sort(({ orderScore: orderScoreX }, { orderScore: orderScoreY }) => {
		if (!orderScoreX || !orderScoreY) {
			return 0;
		}
		if (orderScoreX.major === orderScoreY.major) {
			return orderScoreY.minor - orderScoreX.minor;
		}
		return orderScoreY.major - orderScoreX.major;
	});
	return sortedList;
}

export const getUniqueArray = (array: string[]): string[] => {
	const uniqueArray = array.filter((item, index) => array.indexOf(item) === index);
	return uniqueArray;
};

export const getFormattedTimeDuration = (
	secs: number | null | undefined,
	formatNumber: IntlShape['formatNumber'],
) => {
	if (secs === 0) {
		return 0;
	}
	if (secs) {
		if (secs >= SECONDS_IN_DAY) {
			const days = formatNumber(Math.floor(secs / SECONDS_IN_DAY), {
				style: 'unit',
				unit: 'day',
				unitDisplay: 'narrow',
			});
			const hours = formatNumber(Math.round((secs % SECONDS_IN_DAY) / SECONDS_IN_HOUR), {
				style: 'unit',
				unit: 'hour',
				unitDisplay: 'narrow',
			});
			return `${days} ${hours}`;
		}
		if (secs >= SECONDS_IN_HOUR) {
			const hours = formatNumber(Math.floor(secs / SECONDS_IN_HOUR), {
				style: 'unit',
				unit: 'hour',
				unitDisplay: 'narrow',
			});
			const minutes = formatNumber(Math.round((secs % SECONDS_IN_HOUR) / SECONDS_IN_MINUTE), {
				style: 'unit',
				unit: 'minute',
				unitDisplay: 'narrow',
			});
			return `${hours} ${minutes}`;
		}
		return formatNumber(Math.round(secs / SECONDS_IN_MINUTE), {
			style: 'unit',
			unit: 'minute',
			unitDisplay: 'narrow',
		});
	}
	return null;
};
